/**
 * Lead detail — own tab. Route: /crm/leads/:leadId (tab key 'lead:view:<id>').
 *
 * Actions live in the RecordPage ActionBar:
 *   primary   Log activity (side pane form) · Convert to student (side pane form, hidden once converted)
 *   secondary Stage (inline select) · Assign (side pane, staff only) · Delete (destructive, staff only)
 * Body: Contact & profile (read-only grid + editable notes) · Interested courses · Activity timeline.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabs, useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { paneForm } from '../../components/shell/SidePane';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Phone, EnvelopeSimple, WhatsappLogo, CalendarBlank, Note, ClockCounterClockwise,
  UserSwitch, UserCheck, Trash, ChatCircleText, Books, User, FloppyDisk
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { STAGE_LABELS, SOURCE_LABELS } from './CRMPage';

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: EnvelopeSimple },
  { value: 'whatsapp', label: 'WhatsApp', icon: WhatsappLogo },
  { value: 'meeting', label: 'Meeting', icon: CalendarBlank },
  { value: 'note', label: 'Note', icon: Note },
  { value: 'follow_up', label: 'Follow-up', icon: ClockCounterClockwise },
];

const EMPTY_ACTIVITY = { activity_type: 'call', description: '', follow_up_date: '' };
const EMPTY_CONVERT = { course_ids: [], password: 'welcome123', send_credentials: true };

const LEVEL_DETAILS = {
  school: (l) => [l.school_class && `Class ${l.school_class}`, l.school_board, l.school_name].filter(Boolean).join(' · '),
  college: (l) => [l.college_year, l.college_name].filter(Boolean).join(' · '),
  competitive: (l) => l.competitive_exam || '',
  professional: (l) => [l.professional_course, l.professional_level].filter(Boolean).join(' · '),
};

function InfoItem({ label, value, className = '' }) {
  if (!value) return null;
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <p className={`text-sm ${className}`}>{value}</p>
    </div>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  const isInstructor = currentUser?.role === 'instructor';

  const [leadDetail, setLeadDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [courses, setCourses] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [pane, setPane] = useState(null); // 'activity' | 'convert' | 'assign' | null
  const [submitting, setSubmitting] = useState(false);
  const [activityForm, setActivityForm] = useState({ ...EMPTY_ACTIVITY });
  const [convertData, setConvertData] = useState({ ...EMPTY_CONVERT });
  const [assignTo, setAssignTo] = useState('');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const lead = leadDetail?.lead;
  const leadName = lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : '';
  useTabLabel(lead ? `Lead: ${leadName}` : null);

  const fetchLead = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/leads/${leadId}`);
      setLeadDetail(res.data);
      setNotes(res.data?.lead?.notes || '');
      setNotFound(false);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [api, leadId]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  useEffect(() => {
    const load = async () => {
      try {
        const [coursesRes, staffRes] = await Promise.all([
          api.get('/courses').catch(() => ({ data: [] })),
          api.get('/users').catch(() => ({ data: [] }))
        ]);
        setCourses(coursesRes.data || []);
        setStaffMembers((staffRes.data || []).filter(u => ['instructor', 'tenant_admin'].includes(u.role)));
      } catch {}
    };
    load();
  }, [api]);

  const closePane = () => setPane(null);
  const openPane = (which) => {
    if (which === 'activity') setActivityForm({ ...EMPTY_ACTIVITY });
    if (which === 'convert') setConvertData({ ...EMPTY_CONVERT });
    if (which === 'assign') setAssignTo(lead?.assigned_to || '');
    setPane(which);
  };

  // ─── Actions ───
  const handleStageChange = async (newStage) => {
    if (!lead || newStage === lead.stage) return;
    try {
      await api.put(`/crm/leads/${lead.id}/stage`, { stage: newStage });
      setLeadDetail(prev => ({ ...prev, lead: { ...prev.lead, stage: newStage } }));
      toast.success(`Moved to ${STAGE_LABELS[newStage] || newStage}`);
      fetchLead();
    } catch { toast.error('Failed to update stage'); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!lead) return;
    setSubmitting(true);
    try {
      const payload = { lead_id: lead.id, activity_type: activityForm.activity_type, description: activityForm.description };
      if (activityForm.follow_up_date) payload.follow_up_date = activityForm.follow_up_date;
      await api.post('/crm/activities', payload);
      const res = await api.get(`/crm/leads/${lead.id}`);
      setLeadDetail(res.data);
      toast.success('Activity logged');
      closePane();
    } catch { toast.error('Failed to log activity'); }
    finally { setSubmitting(false); }
  };

  const handleConvert = async (e) => {
    e?.preventDefault?.();
    if (!lead) return;
    setSubmitting(true);
    try {
      await api.post(`/crm/leads/${lead.id}/convert`, { lead_id: lead.id, ...convertData });
      toast.success('Lead converted to student!');
      closePane();
      fetchLead();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert lead'); }
    finally { setSubmitting(false); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!lead) return;
    setSubmitting(true);
    try {
      await api.put(`/crm/leads/${lead.id}/assign?assigned_to=${assignTo}`);
      toast.success('Lead reassigned');
      closePane();
      fetchLead();
    } catch { toast.error('Failed to reassign'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteLead = async () => {
    if (!lead) return;
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.delete(`/crm/leads/${lead.id}`);
      toast.success('Lead deleted');
      closeAndNavigate(`lead:view:${leadId}`, '/crm');
    } catch { toast.error('Failed to delete lead'); }
  };

  const handleSaveNotes = async () => {
    if (!lead) return;
    setSavingNotes(true);
    try {
      await api.put(`/crm/leads/${lead.id}`, { notes });
      setLeadDetail(prev => ({ ...prev, lead: { ...prev.lead, notes } }));
      toast.success('Notes saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save notes'); }
    finally { setSavingNotes(false); }
  };

  const notesDirty = !!lead && (notes || '') !== (lead.notes || '');

  // ─── Side pane (small forms open here instead of popups) ───
  let side = null;
  if (pane === 'activity') {
    const f = paneForm({ id: 'log-activity-form', onSubmit: handleAddActivity, onCancel: closePane, submitLabel: 'Log activity', submitting, submitIcon: ChatCircleText, children: (
      <>
        <div className="field">
          <Label className="text-xs">Type</Label>
          <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm({ ...activityForm, activity_type: v })}>
            <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIVITY_TYPES.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="field">
          <Label className="text-xs">Description *</Label>
          <Textarea value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} rows={4} required placeholder="What happened? Next steps?" data-testid="activity-description" />
        </div>
        <div className="field">
          <Label className="text-xs">Follow-up date</Label>
          <Input type="date" value={activityForm.follow_up_date} onChange={(e) => setActivityForm({ ...activityForm, follow_up_date: e.target.value })} data-testid="activity-follow-up-date" />
        </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Log activity', subtitle: leadName, icon: ChatCircleText, children: f.body, footer: f.footer, testid: 'log-activity-pane' };
  } else if (pane === 'convert') {
    const f = paneForm({ id: 'convert-lead-form', onSubmit: handleConvert, onCancel: closePane, submitLabel: 'Convert & Enroll', submitting, submitIcon: UserCheck, children: (
      <>
        <div className="field">
          <Label className="text-xs">Initial Password</Label>
          <Input value={convertData.password} onChange={(e) => setConvertData({...convertData, password: e.target.value})} />
        </div>
        <div className="field">
          <Label className="text-xs">Enroll in Courses</Label>
          <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
            {courses.map(c => (
              <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                <input type="checkbox" checked={convertData.course_ids.includes(c.id)}
                  onChange={(e) => {
                    if (e.target.checked) setConvertData({...convertData, course_ids: [...convertData.course_ids, c.id]});
                    else setConvertData({...convertData, course_ids: convertData.course_ids.filter(id => id !== c.id)});
                  }} />
                {c.title}
              </label>
            ))}
            {courses.length === 0 && <p className="field-hint">No courses available</p>}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="send-credentials-toggle">
          <input type="checkbox" checked={convertData.send_credentials}
            onChange={(e) => setConvertData({ ...convertData, send_credentials: e.target.checked })} />
          Send welcome email with login credentials
        </label>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Convert lead to student', subtitle: leadName, icon: UserCheck, children: f.body, footer: f.footer, testid: 'convert-lead-pane' };
  } else if (pane === 'assign') {
    const f = paneForm({ id: 'assign-lead-form', onSubmit: handleAssign, onCancel: closePane, submitLabel: 'Assign', submitting, submitIcon: UserSwitch, children: (
      <div className="field">
        <Label className="text-xs">Reassign To</Label>
        <select className="form-select"
          value={assignTo}
          onChange={(e) => setAssignTo(e.target.value)}
          data-testid="assign-to-select">
          <option value="">Unassigned</option>
          {staffMembers.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
        </select>
        {staffMembers.length === 0 && <p className="field-hint">No staff members available</p>}
      </div>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Assign lead', subtitle: leadName, icon: UserSwitch, children: f.body, footer: f.footer, testid: 'assign-lead-pane' };
  }

  if (loading && !leadDetail) {
    return (
      <DashboardLayout>
        <RecordPage title="Lead" onBack={() => navigate('/crm')} loading data-testid="lead-detail" />
      </DashboardLayout>
    );
  }

  if (notFound || !lead) {
    return (
      <DashboardLayout>
        <RecordPage title="Lead not found" onBack={() => navigate('/crm')} findable={false} data-testid="lead-detail">
          <p className="text-sm text-muted-foreground">This lead does not exist or you do not have access to it.</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  const stageSelect = (
    <div className="flex items-center gap-1.5" data-testid="lead-stage-control">
      <span className="text-xs text-muted-foreground">Stage</span>
      <Select value={lead.stage} onValueChange={handleStageChange}>
        <SelectTrigger className="w-[170px]" aria-label="Change stage" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.keys(STAGE_LABELS).map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const interestedCourses = lead.interested_course_details
    || courses.filter(c => (lead.interested_courses || []).includes(c.id));
  const levelDetail = lead.student_level && LEVEL_DETAILS[lead.student_level] ? LEVEL_DETAILS[lead.student_level](lead) : '';
  const activities = leadDetail.activities || [];

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="lead-detail"
        title={leadName || 'Lead'}
        subtitle={[SOURCE_LABELS[lead.source] || lead.source, lead.created_at && `Created ${new Date(lead.created_at).toLocaleDateString()}`].filter(Boolean).join(' · ')}
        badge={(
          <>
            <StatusBadge value={lead.stage} />
            {lead.converted && <Badge variant="secondary" className="text-xs">Converted</Badge>}
          </>
        )}
        onBack={() => navigate('/crm')}
        onRefresh={fetchLead}
        refreshing={loading}
        side={side}
        primary={[
          { key: 'log-activity', label: 'Log activity', icon: ChatCircleText, onClick: () => openPane('activity'), testid: 'log-activity-btn' },
          { key: 'convert', label: 'Convert to student', icon: UserCheck, onClick: () => openPane('convert'), hidden: !!lead.converted, testid: 'convert-lead-btn' },
        ]}
        secondary={[
          { key: 'stage', node: stageSelect },
          { key: 'assign', label: 'Assign', icon: UserSwitch, onClick: () => openPane('assign'), hidden: isInstructor, testid: 'assign-lead-btn' },
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: handleDeleteLead, hidden: isInstructor, testid: 'delete-lead-btn' },
        ]}
      >
        <FormSection
          title="Contact & profile"
          icon={User}
          actions={(
            <Button size="sm" variant="outline" className="h-8" onClick={handleSaveNotes} disabled={!notesDirty || savingNotes} data-testid="save-notes-btn">
              <FloppyDisk className="w-3.5 h-3.5" /> {savingNotes ? 'Saving…' : 'Save notes'}
            </Button>
          )}
        >
          <div className="form-grid">
            <InfoItem label="Email" value={lead.email} />
            <InfoItem label="Phone" value={lead.phone} />
            <InfoItem label="Guardian" value={lead.guardian_name} />
            <InfoItem label="Source" value={SOURCE_LABELS[lead.source] || lead.source} />
            <InfoItem label="Level" value={lead.student_level} className="capitalize" />
            <InfoItem label="Level details" value={levelDetail} />
            <InfoItem label="Assigned To" value={lead.assigned_to_name || (lead.assigned_to ? '' : 'Unassigned')} />
            <InfoItem label="Created" value={lead.created_at && new Date(lead.created_at).toLocaleString()} />
            <InfoItem label="Last updated" value={lead.updated_at && new Date(lead.updated_at).toLocaleString()} />
          </div>
          <div className="space-y-1 mt-4">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Internal notes about this lead" data-testid="lead-notes" />
          </div>
        </FormSection>

        <FormSection title="Interested courses" icon={Books}>
          {interestedCourses.length === 0 ? (
            <p className="text-sm text-muted-foreground">No courses recorded for this lead.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {interestedCourses.map(c => <Badge key={c.id} variant="outline" className="text-xs">{c.title}</Badge>)}
            </div>
          )}
        </FormSection>

        <FormSection title={`Activity timeline (${activities.length})`} icon={ClockCounterClockwise}>
          <div className="space-y-3" data-testid="activity-timeline">
            {activities.map(a => {
              const type = ACTIVITY_TYPES.find(t => t.value === a.activity_type);
              const Icon = type?.icon || Note;
              return (
                <div key={a.id} className="flex gap-3 text-sm border-l-2 border-muted pl-3 py-1">
                  <Icon className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium capitalize">{(a.activity_type || '').replace('_', ' ')}</p>
                    <p className="text-muted-foreground text-xs whitespace-pre-wrap">{a.description}</p>
                    {a.follow_up_date && <p className="text-[10px] text-amber-700 mt-1">Follow-up: {new Date(a.follow_up_date).toLocaleDateString()}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(a.created_at).toLocaleString()} {a.created_by_name ? `by ${a.created_by_name}` : ''}</p>
                  </div>
                </div>
              );
            })}
            {activities.length === 0 && (
              <p className="text-center py-6 text-muted-foreground text-sm">No activities yet</p>
            )}
          </div>
        </FormSection>
      </RecordPage>
    </DashboardLayout>
  );
}
